import { useQuery } from "@tanstack/react-query";
import { getCurrentUser } from "@/lib/supabase/queries/auth";
import { authKeys } from "@/lib/react-query/keys";
import type { AuthUser } from "@/lib/supabase/client";

export type Identity = {
  status: 'loading' | 'ready';
  source: 'supabase' | null;
  user: AuthUser | null;
};

/**
 * Identity hook using React Query for online-first architecture.
 * Provides the authenticated user identity from Supabase.
 * This replaces the PowerSync-based offline identity management.
 */
export const useIdentity = (): Identity => {
  const {
    data: userData,
    isLoading,
    error
  } = useQuery({
    queryKey: authKeys.currentUser(),
    queryFn: getCurrentUser,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Transform the response to match the expected Identity interface
  const user = userData?.data || null;
  const status = isLoading ? 'loading' : 'ready';
  const source = user ? 'supabase' : null;

  return {
    status,
    source,
    user
  };
};